// src/components/DownloadButton.js - download an invoice PDF (used by leader and admin pages).
//
// Audit FL-44: goes through the shared API client (token, 401 handling), shows a Czech
// error instead of failing silently, handles invoice numbers with several slashes or
// none at all, and keeps the object URL alive long enough for Safari/iOS.
import React, { useState } from 'react';
import { Download } from 'lucide-react';
import api from '../services/api';
import { blobErrorMessage, filenameFromHeaders, saveBlob } from '../utils/download';
import { useToast } from './ui/Toast';

const FALLBACK_ERROR = 'Fakturu se nepodařilo stáhnout. Zkuste to prosím znovu.';

const DownloadButton = ({ invoiceId, invoiceNumber, className = '', iconOnly = false, label = 'Stáhnout fakturu' }) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' });
      const safeNumber = String(invoiceNumber || invoiceId || 'faktura').replace(/[\\/:*?"<>|\s]+/g, '-');
      const blob =
        response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      saveBlob(blob, filenameFromHeaders(response.headers, `faktura-${safeNumber}.pdf`));
    } catch (err) {
      const message = await blobErrorMessage(err, FALLBACK_ERROR);
      if (iconOnly) toast.error(message);
      else setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={handleDownload}
        disabled={isLoading}
        className="rounded-md p-1 text-indigo-600 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
        title="Stáhnout fakturu (PDF)"
        aria-label={`Stáhnout fakturu ${invoiceNumber || ''}`.trim()}
      >
        <Download className={`h-5 w-5 ${isLoading ? 'animate-pulse' : ''}`} aria-hidden="true" />
      </button>
    );
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={handleDownload}
        disabled={isLoading}
        className={`inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      >
        <Download className={`h-4 w-4 ${isLoading ? 'animate-pulse' : ''}`} aria-hidden="true" />
        {isLoading ? 'Stahuji…' : label}
      </button>
      {error && (
        <span role="alert" className="text-sm text-red-700">
          {error}
        </span>
      )}
    </span>
  );
};

export default DownloadButton;
